'use client';

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';

/**
 * ChatSelectionContext
 * 
 * Centralized management of chat selection and URL synchronization.
 * This context handles:
 * - Reading chatId from URL query parameters
 * - Updating the URL when chatId changes
 * - Providing the current selected chat ID to all components
 * 
 * Usage:
 * 1. Wrap your component tree with <ChatSelectionProvider>
 * 2. Use useChatSelection() hook to access and update the chatId
 */

export interface ChatSelectionContextType {
  /** Current chatId from URL or null if no chat selected */
  chatId: string | null;
  
  /** Set the chatId and update URL */
  setChatId: (chatId: string | null) => void;
}

const ChatSelectionContext = createContext<ChatSelectionContextType | undefined>(undefined);

interface ChatSelectionProviderProps {
  children: React.ReactNode;
  initialChatId?: string;
}

/**
 * Provider component for ChatSelectionContext
 * 
 * Props:
 * - children: React components to wrap
 * - initialChatId: Optional initial chatId (useful for SSR or prop-based initialization)
 */
export function ChatSelectionProvider({ children, initialChatId }: ChatSelectionProviderProps) {
  const getChatIdFromUrl = useCallback((): string | null => {
    try {
      const url = new URL(window.location.href);
      return url.searchParams.get('chatId');
    } catch {
      return null;
    }
  }, []);

  const [chatId, setChatIdState] = useState<string | null>(() => {
    // Initialize from prop or URL immediately
    return initialChatId ?? getChatIdFromUrl();
  });

  const isDev = process.env.NODE_ENV === "development";
  if (isDev) {
    console.log("[ChatSelectionProvider] current chatId state:", chatId);
  }

  // Sync with URL changes - only when the actual URL chatId value changes
  useEffect(() => {
    const onPopState = () => {
      const nextChatId = getChatIdFromUrl();
      if (nextChatId !== chatId) {
        if (isDev) console.log("[ChatSelectionProvider] Syncing chatId from URL:", nextChatId);
        setChatIdState(nextChatId);
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [chatId, getChatIdFromUrl]);

  // Update URL query parameter when chatId changes
  const setChatId = useCallback((newChatId: string | null) => {
    if (isDev) console.log("[ChatSelectionProvider] setChatId called with:", newChatId);
    // Update local state
    setChatIdState(newChatId);
    
    // Update URL
    const newUrl = new URL(window.location.href);
    if (newChatId === null) {
      newUrl.searchParams.delete('chatId');
    } else {
      newUrl.searchParams.set('chatId', newChatId);
    }
    if (isDev) console.log("[ChatSelectionProvider] Updated URL to:", newUrl.toString());
    window.history.replaceState({}, '', newUrl.toString());
  }, []);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      chatId,
      setChatId,
    }),
    [chatId, setChatId]
  );

  return (
    <ChatSelectionContext.Provider value={value}>
      {children}
    </ChatSelectionContext.Provider>
  );
}

/**
 * Hook to access and update the chat selection
 * 
 * Returns:
 * - chatId: Current chatId from URL or null
 * - setChatId: Function to update chatId and URL
 * 
 * Example:
 * ```tsx
 * const { chatId, setChatId } = useChatSelection();
 * 
 * // Select a chat
 * setChatId('chat-123');
 * 
 * // Clear selection
 * setChatId(null);
 * ```
 */
export function useChatSelection() {
  const context = useContext(ChatSelectionContext);
  if (context === undefined) {
    throw new Error('useChatSelection must be used within a ChatSelectionProvider');
  }
  return context;
}

// Backwards compatibility aliases
export const ChatIdProvider = ChatSelectionProvider;
export const useChatId = useChatSelection;
export type ChatIdContextType = ChatSelectionContextType;

